import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Loader2 } from 'lucide-react';
import { AssessmentCard } from '@/components/admin/AssessmentCard';
import { fetchPhysicalAssessments, fetchAssessmentStats } from '@/lib/api';

export default function PhysicalAssessments() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSystem, setFilterSystem] = useState('all');
  const [filterAbnormality, setFilterAbnormality] = useState('all');
  const [loading, setLoading] = useState(true);
  const [assessments, setAssessments] = useState<any[]>([]);
  const [stats, setStats] = useState({ total: 0, cardiovascular: 0, respiratory: 0, neurological: 0, other: 0 });

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const [assessmentsData, statsData] = await Promise.all([
          fetchPhysicalAssessments({ search: searchQuery, system: filterSystem, type: filterAbnormality }),
          fetchAssessmentStats(),
        ]);
        setAssessments(assessmentsData);
        setStats(statsData);
      } catch (error) {
        console.error('Failed to load assessments:', error);
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [searchQuery, filterSystem, filterAbnormality]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="animate-spin h-12 w-12 mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading physical assessments...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-foreground mb-2">Physical Assessments</h1>
            <p className="text-lg text-muted-foreground">
              Manage physical examination findings and clinical assessments
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/assessments/create')}
            className="flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-colors shadow-lg hover:shadow-xl"
          >
            <Plus size={20} />
            Add Assessment
          </button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-sm text-muted-foreground mb-1">Total Assessments</div>
            <div className="text-3xl font-bold text-foreground">{stats.total}</div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-sm text-muted-foreground mb-1">Cardiovascular</div>
            <div className="text-3xl font-bold text-destructive">{stats.cardiovascular}</div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-sm text-muted-foreground mb-1">Respiratory</div>
            <div className="text-3xl font-bold text-primary">{stats.respiratory}</div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-sm text-muted-foreground mb-1">Neurological</div>
            <div className="text-3xl font-bold text-accent">{stats.neurological}</div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="text-sm text-muted-foreground mb-1">Other Systems</div>
            <div className="text-3xl font-bold text-foreground">{stats.other}</div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
                <input
                  type="text"
                  placeholder="Search assessments by name, system, or findings..."
                  className="w-full pl-10 pr-4 py-3 border border-input bg-background rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent text-foreground placeholder:text-muted-foreground"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            {/* System Filter */}
            <select
              value={filterSystem}
              onChange={(e) => setFilterSystem(e.target.value)}
              className="px-4 py-2 border border-input bg-background rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent text-foreground"
            >
              <option value="all">All Systems</option>
              <option value="general_constitutional">General / Constitutional</option>
              <option value="heent">HEENT</option>
              <option value="neck">Neck</option>
              <option value="cardiovascular">Cardiovascular</option>
              <option value="respiratory">Respiratory</option>
              <option value="abdominal_gi">Abdominal / GI</option>
              <option value="musculoskeletal">Musculoskeletal</option>
              <option value="neurological">Neurological</option>
              <option value="skin">Skin</option>
              <option value="psych_mental">Psych / Mental State</option>
              <option value="genitourinary">Genitourinary</option>
            </select>

            {/* Abnormality Filter */}
            <select
              value={filterAbnormality}
              onChange={(e) => setFilterAbnormality(e.target.value)}
              className="px-4 py-2 border border-input bg-background rounded-lg focus:ring-2 focus:ring-ring focus:border-transparent text-foreground"
            >
              <option value="all">All Findings</option>
              <option value="normal">Normal</option>
              <option value="abnormal">Abnormal</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>

        {/* Results count */}
        <div className="mb-4 text-sm text-muted-foreground">
          Showing {assessments.length} of {stats.total} assessments
        </div>

        {/* Assessments Grid */}
        {assessments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {assessments.map((assessment) => (
              <AssessmentCard key={assessment.id} assessment={assessment} />
            ))}
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <p className="text-muted-foreground text-lg">No assessments found matching your filters.</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setFilterSystem('all');
                setFilterAbnormality('all');
              }}
              className="mt-4 text-primary hover:underline"
            >
              Clear filters
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
